/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * ClientAdapter.tsx
 */

import ListAdapter, {AdapterListener} from "../../tools/list/ListAdapter";
import React from "react";
import {Column} from "../../tools/list/Column"
import Translation from "../../services/translation/Translation";
import Button from "../../tools/view/element/button/Button";
import Colors from "../../tools/model/enumerations/Colors";
import UIToast from "../../tools/view/toast/UIToast";
import DeviceDataProvider from "../../provider/DeviceDataProvider";
import Device from "../../models/Device";
import {APIPagination} from "../../tools/pagination/APIPagination";

export interface DeviceAdapterListener extends AdapterListener<Device> {
    removeDevice: (device: Device) => void;
}

export default class DeviceAdapter extends ListAdapter<Device, DeviceAdapterListener, APIPagination> {
    private _t: Translation;

    /**
     * constructor
     * @param devicesProvider
     * @param listener
     * @param t
     */
    constructor(t: Translation, devicesProvider: DeviceDataProvider, listener?: DeviceAdapterListener) {
        super();

        this._t = t;
        this.addColumn(new Column(t.name, "name", this._name))
        this.addColumn(new Column(t.identifier, "id", this._id));
        this.addColumn(new Column(t.uuid, "uuid", this._uuid));
        this.addColumn(new Column(t.clientId, "clientId", this._clientId));
        this.addColumn(new Column(t.action, "id", this._remove));

        this.init(t.deviceListEmpty,
            devicesProvider,
            [],
            listener);
    }

    /**
     * _name
     * @returns {any}
     * @private
     * @param device
     */
    private _name = (device: Device): JSX.Element => {
        return <p className={`blue-hover ${device.name ? "" : "has-text-grey-lighter"}`}
                  onClick={() => {
                      if (device.name)
                        this._copyToClipboard(device.name);
                  }}>
            {device.name || 'N/A'}
        </p>
    };

    /**
     * _id
     * @returns {any}
     * @private
     * @param device
     */
    private _id = (device: Device): JSX.Element => {
        return <p className={"blue-hover"}
                  onClick={() => this._copyToClipboard(device.id)}>
            {device.id}
        </p>
    };

    /**
     * _uuid
     * @returns {any}
     * @private
     * @param device
     */
    private _uuid = (device: Device): JSX.Element => {
        return <p className={"blue-hover"}
                  onClick={() => this._copyToClipboard(device.uuid)}>
            {device.uuid}
        </p>
    };

    /**
     * _clientId
     * @returns {any}
     * @private
     * @param device
     */
    private _clientId = (device: Device): JSX.Element => {
        return <p className={"blue-hover"}
            onClick={() => this._copyToClipboard(device.client_id)}>
            {device.client_id}
        </p>
    };

    /**
     * _copyToClipboard
     * @param s
     * @private
     */
    private _copyToClipboard = (s: string) => {
        navigator.clipboard.writeText(s);
        UIToast.default.show(this._t.copied)
    };

    /**
     * _enabled
     * @returns {any}
     * @private
     * @param device
     */
    private _remove = (device: Device): JSX.Element => {
        return <Button color={Colors.red} onClick={() => this._listener?.removeDevice(device)}
                       labelAccessor={t => t.delete}/>
    };
}